import { useContextGR } from '@google-recaptcha';
import { useEventEmailCaptured } from '@google-tag-manager';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import { SubscribeService } from '@core/api';
import {
  Box,
  Button,
  ButtonSubmit,
  FormControl,
  Image,
  Loader,
  spotlightBannerCollapsingAnimation,
  Typography,
} from '@core/component';
import { Translate } from '@core/constant';
import { useContextUser, useContextUtil } from '@core/context';
import { useLanguage } from '@core/hook';
import { getStaticPathIconFolder } from '@core/media';
import {
  EButtonVariant,
  EColor,
  EFontWeight,
  EGTMCaptureLocation,
  ESize,
  ETypographyVariant,
} from '@core/type';

import { ModalSpotlight } from '../modal';
import { SubmitSuccessful } from './SubmitSuccessful';
import { InputForm } from './interface-spotlight-banner';
import { useSpotlightForm } from './use-utils-form';

const ANIMATION_TIME = 2000;

export const MiniBannerSpotlight = () => {
  const { getRecaptcha } = useContextGR();
  const { axiosInstance } = useContextUtil();
  const { pushEventEmailCaptured } = useEventEmailCaptured();
  const { language } = useLanguage();
  const { email: emailUser } = useContextUser();
  const { t } = useTranslation(Translate.common.PRODUCT);

  const [isOpen, setOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    isClosed,
    setIsClosed,
    isClosing,
    setIsClosing,
    handleSubmit,
    trigger,
    reset,
    setError,
    isSubmitSuccessful,
    tForm,
    isSubmitting,
    onSuccess,
  } = useSpotlightForm();

  if (isClosed) {
    return null;
  }

  const onSuccessHandler = () => setIsSuccess(true);
  const onOpenModal = () => setOpen(true);
  const onCloseModal = () => setOpen(false);

  return (
    <>
      <ModalSpotlight isOpen={isOpen} onCloseModal={onCloseModal} onSuccess={onSuccessHandler} />
      <Box
        position={'relative'}
        backgroundColor={EColor.INFO}
        padding={'24px 14px 56px'}
        animation={isClosing ? spotlightBannerCollapsingAnimation : undefined}
        animationDuration={`${ANIMATION_TIME}ms`}
        borderRadius={'8px'}
      >
        <FormControl
          t={tForm}
          trigger={trigger}
          reset={reset}
          handleSubmit={handleSubmit}
          setError={setError}
          fn={async (data: InputForm) => {
            const token = await getRecaptcha();
            const submitData = {
              ...data,
              token,
            };

            return SubscribeService.subscribeEmail(axiosInstance, language, submitData).then(
              (data) => {
                pushEventEmailCaptured(EGTMCaptureLocation.NEWSLETTER);
                return data;
              },
            );
          }}
          onSuccess={onSuccess}
        >
          <Box display={'flex'} justifyContent={'center'}>
            <Image
              src={getStaticPathIconFolder(
                `spotlight/${isSuccess ? 'ic-subscribe-successful.svg' : 'ic-subscribe.svg'}`,
              )}
              width={'72'}
              height={'72'}
              alt={'image-letter'}
            />
          </Box>
          {isSubmitSuccessful || isSuccess ? (
            <SubmitSuccessful setIsClosed={setIsClosed} setIsClosing={setIsClosing} isSmallBanner />
          ) : (
            <>
              <Box position={'relative'} margin={'16px 0 8px'}>
                <Typography
                  fontStyle={'italic'}
                  variant={ETypographyVariant.BODY2}
                  fontWeight={EFontWeight.BOLD}
                  textAlign={'center'}
                >
                  {t('theSpotlight')}
                </Typography>
              </Box>
              <Typography
                variant={ETypographyVariant.CAPTION1}
                textAlign={'center'}
                marginBottom={'24px'}
              >
                {t('subscribe.description')}
              </Typography>

              {isOpen || isSubmitting ? (
                <Box display={'flex'} justifyContent={'center'}>
                  <Loader />
                </Box>
              ) : (
                <Box margin={'0 auto'}>
                  <Box display={'flex'} justifyContent={'center'}>
                    {emailUser ? (
                      <ButtonSubmit
                        size={ESize.SM}
                        variant={EButtonVariant.OUTLINED}
                        padding={'0 20px'}
                      >
                        {t('subscriptionBanner.btnSubscribe')}
                      </ButtonSubmit>
                    ) : (
                      <Button
                        size={ESize.SM}
                        variant={EButtonVariant.OUTLINED}
                        padding={'0 20px'}
                        onClick={onOpenModal}
                      >
                        {t('subscriptionBanner.btnSubscribe')}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </FormControl>
      </Box>
    </>
  );
};
